import { whenAmazonTakt } from '@amzn/takt-utils';
import { useGetReportMetadataQuery } from '@urc-ui/store/api/report-metadata/reportMetadata';
import {
  ADVERTISER_NAMESPACE,
  CONTEXT_NAME_SPACE,
  TAKT_EVENT,
  TaktEventProps,
  TaktLoggerProps,
  TaktRequestModel,
  TaktSubscriptionEvent
} from '@urc-ui/hooks/takt-logger/types';
import { buildGetSubscriptionByIdRequest, useGetSubscriptionsQuery } from '@urc-ui/store/api/subscriptions/subscriptions';
import { useGetSubscriptionByIdQuery } from '@urc-ui/store/api/ross/subscriptions/subscriptions';
import { Subscription } from '@urc-ui/store/api/subscriptions/types';
import { useCallback } from 'react';
import { getTaktReportTypeFromReportMetadata } from '@urc-ui/utils/report-metadata-utils';
import { determineSubscriptionType } from '@urc-ui/utils/subscription-utils';
import { isADSPReportCenterEnabled, isUiRossCutoverEnabled } from '@urc-ui/utils/app-utils';
import { ReportMetadata } from '@urc-ui/store/api/report-metadata/types';
import { transformSubscription } from '@urc-ui/store/api/ross/transformers/ross-to-sspa-transformer';

const useURCSubscription = (props: TaktLoggerProps): Subscription | null => {
  if (!props.subscriptionId) return null;
  const request = buildGetSubscriptionByIdRequest(props.subscriptionId);
  const fetchedSubscription = useGetSubscriptionsQuery(request, { skip: !!props.subscription || !props.subscriptionId });
  if (props.subscription) {
    return props.subscription;
  }
  const { data } = fetchedSubscription;
  return data && data.subscriptions.length ? data.subscriptions[0] : null;
};

const useRossSubscription = (props: TaktLoggerProps): Subscription | null => {
  if (!props.subscriptionId) return null;
  const fetchedSubscription = useGetSubscriptionByIdQuery(
    { subscriptionId: props.subscriptionId },
    { skip: !!props.subscription || !props.subscriptionId }
  );
  if (props.subscription) {
    return props.subscription;
  }
  const { data } = fetchedSubscription;
  return data && data.subscription ? transformSubscription(data.subscription) : null;
};

/**
 * We must get either subscriptionId or subscription in props if that custom event needs subscription details to be added in the payload.
 * If not both can be empty for events like Search report user action which does not have any subscription details in the payload.
 */
const useTaktLogger = (props: TaktLoggerProps) => {
  const { data: reportMetadata } = useGetReportMetadataQuery();
  const subscription = isUiRossCutoverEnabled() ? useRossSubscription(props) : useURCSubscription(props);

  const logCustomTaktEvent = useCallback(
    (taktData: TaktEventProps | TaktSubscriptionEvent) => {
      const context = taktData && taktData.eventName ? `${CONTEXT_NAME_SPACE}-${taktData.eventName}` : CONTEXT_NAME_SPACE;
      const namespace = isADSPReportCenterEnabled() ? ADVERTISER_NAMESPACE.ADSP : ADVERTISER_NAMESPACE.SSPA;
      let taktPayload: TaktRequestModel;
      if (subscription) {
        const taktReportTypeFromRMS = getTaktReportTypeFromReportMetadata(
          subscription.programType,
          subscription.reportType,
          reportMetadata as ReportMetadata
        );
        taktPayload = {
          ...taktData,
          programType: subscription.programType,
          reportType: subscription.reportType,
          taktReportType: taktReportTypeFromRMS,
          reportUnit: subscription.timeUnit,
          reportPeriod: subscription.reportPeriod,
          subscriptionType: determineSubscriptionType(subscription.subscriptionDelivery),
          subscriptionFrequency: subscription.subscriptionDelivery.frequency,
          emailCount: subscription.recipients ? subscription.recipients.length : 0,
          subscriptionId: subscription.id,
          marketplaceIds: subscription.marketplaceIds,
          currencyOfVisualization: subscription.currencyOfVisualization,
          namespace
        };
        if (taktData.eventName === TAKT_EVENT.DOWNLOAD_REPORT) {
          if ('reportId' in taktData && taktData?.reportId) {
            taktPayload.reportId = taktData.reportId;
          } else if (subscription.latestReportId) {
            taktPayload.reportId = subscription.latestReportId;
          }
        }
      } else {
        taktPayload = {
          ...taktData,
          namespace
        };
      }
      whenAmazonTakt(amazonTakt => {
        amazonTakt.logCustom({
          context: context,
          details: JSON.stringify(taktPayload)
        });
      });
    },
    [subscription, reportMetadata]
  );

  return { logCustomTaktEvent };
};

export { useTaktLogger };
